import { Divider, InteractionTag, InteractionTagPrimary, InteractionTagSecondary, Link, MessageBar, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { DragEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appInsights } from '../../AppInsights';
import { useNotification } from '../../Contexts/NotificationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { AttachmentSourceType, GptStatus, GptTypes, IAttachment } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations, useFeature } from '../../store/hooks';
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_UploadDocument, UserInteractionEventProperties } from '../../store/models/customEvents';
import { FeatureHighlightPopup } from '../Common/FeatureHighlightPopup';
import { FileTypeIcon } from '../Common/FileTypeIcon';
import { LexicalEditor, LexicalEditorRef } from '../Common/LexicalEdit/LexicalEditor';
import { AttachmentButton } from './AttachmentButton';
import ModelSelection from './ModelSelection';
import { PluginsMenu } from './PluginsMenu';
import SendButton from './SendButton';
import './UserQuery.scss';

export interface UserPromptProps {
    conversationStarterQuery: () => string;
    canSubmit: boolean;
    completionInProgress: boolean;
    isUploadAttachmentOver: boolean;
    onSendQuery: (query: string, plugins: string[], modelName: string) => boolean | Promise<boolean>;
    onUploadDocumentAdd: (files: FileList, source: AttachmentSourceType) => void;
    onUploadDocumentRemove: (filename: string) => void;
    hideModelSelection: boolean;
    restoreLastQueryTrigger?: number;
    inputValue?: string;
    onInputChange?: (value: string) => void;
    clearEditorTrigger?: number;
    restoreReason?: 'error' | 'stop';
}

// manages the user query input, including the attachment uploading and remove, and the send button
// attachment button is used within this component and events from the child component are managed here centrally
export const UserQuery: FC<UserPromptProps> = (props: UserPromptProps) => {
    const chatFeature = useFeature('chat');
    const maxMessageLength = chatFeature.conversation?.maxMessageLength;
    const maxConversationLength = chatFeature.conversation?.maxConversationLength;
    const { conversationStarterQuery, canSubmit, completionInProgress, isUploadAttachmentOver, onSendQuery, onUploadDocumentAdd, onUploadDocumentRemove, hideModelSelection, inputValue, onInputChange, clearEditorTrigger, restoreReason } = props;
    const { activeChat } = useConversations();
    const [internalQuery, setInternalQuery] = useState<string>('');
    const [lastQuery, setLastQuery] = useState<string>(''); // Store last sent query
    const [activePlugins, setActivePlugins] = useState<string[]>([]);
    const [attachments, setAttachments] = useState<IAttachment[]>([]);
    const [isUploadAttachmentEnabled, setisUploadAttachmentEnabled] = useState<boolean>(false);
    const [isAttachmentUploading, setIsAttachmentUploading] = useState<boolean>(false);
    const [warningMessage, setWarningMessage] = useState<WarningMessage | undefined>(undefined);
    const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const { t } = useTranslation();
    const { activeGpt } = useConversations();
    const { userProfile } = useUserContext();
    const [chosenModel, setChosenModel] = useState<string>('');
    const { dispatchNotification } = useNotification();


    // Only use internal state for editor content
    const [editorQuery, setEditorQuery] = useState<string>('');
    const editorRef = useRef<LexicalEditorRef>(null);
    const lastEditorContentRef = useRef<string>('');
    const restoreTriggerRef = useRef<number>(0);
    const [restoreMessage, setRestoreMessage] = useState<string | undefined>(undefined);

    const canSend = useMemo(() => canSubmit && editorQuery.trim().length > 0, [canSubmit, editorQuery]);
    const nbLines = useMemo(() => editorQuery?.split('\n').length ?? 1, [editorQuery]);

    const handleClearEditor = useCallback(() => {
        if (editorRef.current) {
            editorRef.current.clearContent();
            setEditorQuery('');
            lastEditorContentRef.current = '';
        }
        setRestoreMessage(undefined);
    }, []);

    // Clear editor when trigger changes
    useEffect(() => {
        if (clearEditorTrigger !== undefined && clearEditorTrigger > 0) {
            handleClearEditor();
        }
    }, [clearEditorTrigger, handleClearEditor]);

    // Reset processing state when completion is finished
    useEffect(() => {
        if (!completionInProgress && isProcessing) {
            setIsProcessing(false);
        }
    }, [completionInProgress, isProcessing]);

    useEffect(() => {
        // set a css variable to the number of lines
        document.documentElement.style.setProperty('--userquery-nb-lines', nbLines.toString());
    }, [nbLines]);

    useEffect(() => {
        setIsAttachmentUploading(!isUploadAttachmentOver);
    }, [isUploadAttachmentOver]);

    useEffect(() => {
        if (activeChat && activeGpt) {
            setAttachments(activeChat.attachments ?? []);
            setIsAttachmentUploading(false);

            const maxAttachments = activeGpt?.attachment?.maxFiles ?? 1;
            setisUploadAttachmentEnabled(attachments.length < maxAttachments);

            if (activeChat.prompts && maxConversationLength && activeChat.prompts.length > maxConversationLength) {
                setWarningMessage({
                    text: t('conversation.tooManyMessages'),
                    link: 'https://wiki.syensqo.com/pages/viewpage.action?pageId=899156406',
                });
            }
            if (activeChat.gpt?.type === GptTypes.PersonalGpt) {
                // if a conversation has started before the last modification of the personal GPT, send a warning message
                if (activeGpt?.lastUpdateOn && activeChat.startedOn < activeGpt.lastUpdateOn) {
                    setWarningMessage({
                        text: t('personalGpt.warnings.oldConversation'),
                    });
                }

                // warning message about running status overrides the possible warning message about old conversation
                if (activeGpt?.status === GptStatus.Running) {
                    setWarningMessage({
                        text: t('personalGpt.warnings.statusRunning'),
                    });
                }
            }
        }
    }, [activeChat, activeGpt, attachments, maxConversationLength, t]);

    // Only set editor content programmatically for starter, restore, etc.
    useEffect(() => {
        const starterText = conversationStarterQuery();
        if (starterText && editorRef.current) {
            editorRef.current.setContent(starterText);
            setEditorQuery(starterText);
            lastEditorContentRef.current = starterText;
            setRestoreMessage(undefined);
            // Position cursor at the end after setting starter content
            setTimeout(() => {
                editorRef.current?.focusAtEnd();
            }, 0);
        }
    }, [conversationStarterQuery]);

    // Restore last query when triggered
    const onChangeQuery = (newQuery: string) => {
        const message: WarningMessage | undefined = maxMessageLength && newQuery.length >= maxMessageLength ? { text: t('conversation.tooLongMessage') } : undefined;
        setWarningMessage(message);
        setEditorQuery(newQuery);
        lastEditorContentRef.current = newQuery;
        setRestoreMessage(undefined);
    };

    // Function specifically for external updates (like voice recognition)
    const onChangeQueryExternal = (newQuery: string) => {
        const message: WarningMessage | undefined = maxMessageLength && newQuery.length >= maxMessageLength ? { text: t('conversation.tooLongMessage') } : undefined;
        setWarningMessage(message);
        setEditorQuery(newQuery);
        lastEditorContentRef.current = newQuery;
    setRestoreMessage(undefined);

        // Update the LexicalEditor content
        if (editorRef.current) {
            editorRef.current.setContent(newQuery);
            // Position cursor at the end after setting content
            setTimeout(() => {
                editorRef.current?.focusAtEnd();
            }, 0);
        }
    };

    // Modified: Only clear the query if onSendQuery resolves successfully; don't clear on error
    // Helper type guard for Promise
    function isPromise<T = unknown>(value: unknown): value is Promise<T> {
        return !!value && typeof value === 'object' && typeof (value as Promise<T>).then === 'function';
    }

    const handleSendCompletion = useCallback(
        (success: boolean | undefined, sentQuery: string) => {
            setIsProcessing(false);

            if (success) {
                setLastQuery('');
                return;
            }

            const message = t('conversation.inputRestoredAfterError', 'Message restored after an error. You can edit and resend.');
            setRestoreMessage(message);
            if (editorRef.current) {
                editorRef.current.setContent(sentQuery);
                setTimeout(() => editorRef.current?.focusAtEnd(), 0);
            }
            setEditorQuery(sentQuery);
            lastEditorContentRef.current = sentQuery;
        },
        [handleClearEditor, t],
    );

    const handleSendQuery = useCallback(() => {
        if (canSend) {
            const sentQuery = editorQuery;
            setLastQuery(sentQuery);
            setIsProcessing(true);
            setRestoreMessage(undefined);
            handleClearEditor();

            const result = onSendQuery(sentQuery, activePlugins, chosenModel);

            if (isPromise(result)) {
                result
                    .then((success) => {
                        handleSendCompletion(success, sentQuery);
                    })
                    .catch((error) => {
                        console.error('Error sending query:', error);
                        handleSendCompletion(false, sentQuery);
                    });
            } else {
                handleSendCompletion(result, sentQuery);
            }
        }
    }, [canSend, editorQuery, onSendQuery, activePlugins, chosenModel, handleSendCompletion]);

    // Handler to restore last query when stop responding is clicked
    const handleStopResponding = useCallback(() => {
        if (editorRef.current) {
            editorRef.current.setContent(lastQuery);
            setEditorQuery(lastQuery);
            // Position cursor at the end after restoring content
            setTimeout(() => {
                editorRef.current?.focusAtEnd();
            }, 0);
        }
    }, [lastQuery]);


    useEffect(() => {
        if (
            props.restoreLastQueryTrigger !== undefined &&
            props.restoreLastQueryTrigger > 0 &&
            props.restoreLastQueryTrigger !== restoreTriggerRef.current
        ) {
            restoreTriggerRef.current = props.restoreLastQueryTrigger;
            handleStopResponding();

            if (restoreReason === 'stop') {
                setRestoreMessage(t('conversation.inputRestoredAfterStop', 'Message restored because the response was stopped.'));
            } else if (restoreReason === 'error') {
                setRestoreMessage(t('conversation.inputRestoredAfterError', 'Message restored after an error. You can edit and resend.'));
            } else {
                setRestoreMessage(undefined);
            }
        }
    }, [props.restoreLastQueryTrigger, handleStopResponding, restoreReason, t]);

    const activePluginsChanged = (plugins: string[]) => {
        setActivePlugins(plugins);
    };

    const onUploadDocumentClick = useCallback(
        (files: FileList, source: AttachmentSourceType) => {
            setIsAttachmentUploading(true);
            onUploadDocumentAdd(files, source);
            appInsights.trackEvent({
                name: EventName_UserInteraction,
                properties: {
                    applicationType: EventProperty_Value_ApplicationType,
                    userId: userProfile?.username,
                    interactionType: EventProperty_Value_UploadDocument,
                    isSuccess: true,
                } as UserInteractionEventProperties,
            });
        },
        [onUploadDocumentAdd, userProfile],
    );

    // Handle image paste
    const handlePaste = useCallback(
        (event: ClipboardEvent) => {
            // Check if there are image items in the clipboard
            const items = event.clipboardData?.items;
            if (items) {
                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.indexOf('image') !== -1 && isUploadAttachmentEnabled && !completionInProgress) {
                        // Get the image as blob
                        const blob = items[i].getAsFile();
                        if (blob) {
                            // Create a FileList-like object for the onUploadDocumentClick function
                            const dT = new DataTransfer();
                            // Generate a name for the file using timestamp to ensure uniqueness
                            const fileName = `pasted-image-${new Date().getTime()}.png`;
                            const file = new File([blob], fileName, { type: blob.type });
                            dT.items.add(file);

                            // Use the existing upload document function
                            onUploadDocumentClick(dT.files, AttachmentSourceType.LocalDrive);

                            // Prevent default paste behavior for images
                            event.preventDefault();
                            break;
                        }
                    }
                }
            }
        },
        [onUploadDocumentClick, isUploadAttachmentEnabled, completionInProgress],
    );

    // Handle key down events for Lexical editor
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter' && event.shiftKey === false) {
                // Toujours empêcher le comportement par défaut pour Enter seul
                event.preventDefault();
                
                // Envoyer la requête seulement si les conditions sont remplies
                if (canSend && editorQuery && editorQuery.trim().length > 0 && editorQuery !== '\n') {
                    handleSendQuery();
                }
            }
            // Shift + Enter permet le retour à la ligne (comportement par défaut)
        },
        [canSend, handleSendQuery, editorQuery],
    );

    // Drag and drop event handlers
    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDraggingOver(false);

            if (event.dataTransfer.files && event.dataTransfer.files.length > 0 && isUploadAttachmentEnabled && !completionInProgress) {
                onUploadDocumentClick(event.dataTransfer.files, AttachmentSourceType.LocalDrive);
            }
        },
        [onUploadDocumentClick, isUploadAttachmentEnabled, completionInProgress],
    );

    const handleModelChange = useCallback((model: string) => {
        setChosenModel(model);
    }, []);

    // Calculate the appropriate mode for SendButton
    const getSendButtonMode = useCallback(() => {
        if (isProcessing || completionInProgress) {
            return 'processing';
        }
        return editorQuery.length > 0 ? 'typing' : 'ready';
    }, [isProcessing, completionInProgress, editorQuery.length]);

    // Focus the editor as soon as the page is rendered, but only if there is no starter text, last query, or inputValue to set
    useEffect(() => {
        if (
            editorRef.current &&
            !internalQuery && // Only focus if there is no content
            !inputValue
        ) {
            editorRef.current.focus();
        }
    }, []);
    
    return (
        <div className='userQuery'>
            <div className='input-container'>
                <div className='input'>
                    {
                        // display the feature highlight popup for multiple document upload, if enabled
                        activeGpt?.attachment && activeGpt.attachment.isEnabled &&
                        activeGpt?.type !== GptTypes.ImageGenerationGpt &&
                        (
                            <FeatureHighlightPopup
                                id={'multipleDocumentUpload'}
                                header={t('featureHighlights.header')}
                                title={t('featureHighlights.multipleDocumentUpload.title')}
                                content={t('featureHighlights.multipleDocumentUpload.content')}
                                link={t('featureHighlights.multipleDocumentUpload.link')}
                            >
                                <AttachmentButton
                                    enabled={isUploadAttachmentEnabled && !completionInProgress}
                                    onClick={onUploadDocumentClick}
                                    setIsDriveFileDownloading={setIsAttachmentUploading}
                                />
                            </FeatureHighlightPopup>
                        )
                    }
                    <div className={`content ${isDraggingOver ? 'dragging-over' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                        {warningMessage && (
                            <MessageBar intent='warning' className='warning'>
                                <MessageBarBody>
                                    <MessageBarTitle>Warning</MessageBarTitle>
                                    {warningMessage.text}
                                    {warningMessage.link && (
                                        <Link href={warningMessage.link} target='_blank'>
                                            Learn more
                                        </Link>
                                    )}
                                </MessageBarBody>
                            </MessageBar>
                        )}

                        {restoreMessage && (
                            <MessageBar intent='info' className='restore-message'>
                                <MessageBarBody>{restoreMessage}</MessageBarBody>
                            </MessageBar>
                        )}

                        <LexicalEditor
                            ref={editorRef}                            
                            className='lexicaleditor'
                            placeholder='Ask a question'
                            maxLength={maxMessageLength}
                            onChange={onChangeQuery}
                            onPaste={handlePaste}
                            onKeyDown={handleKeyDown}
                        />

                        {(isAttachmentUploading || attachments.length > 0) && <Divider />}
                        <div className='attachmentsContainer'>
                            {
                                // display the attachments added by the user
                                attachments.map((a) => (
                                    <InteractionTag key={a.name} disabled={completionInProgress}>
                                        <InteractionTagPrimary icon={<FileTypeIcon filename={a.name!} />} hasSecondaryAction>
                                            {a.name}
                                        </InteractionTagPrimary>
                                        <InteractionTagSecondary aria-label='dismiss' onClick={() => onUploadDocumentRemove(a.name!)} />
                                    </InteractionTag>
                                ))
                            }
                            {isAttachmentUploading && (
                                <InteractionTag>
                                    <Spinner size='small'></Spinner>
                                    <InteractionTagPrimary>Uploading...</InteractionTagPrimary>
                                </InteractionTag>
                            )}
                        </div>
                        {isDraggingOver && (
                            <div className='drop-overlay'>
                                <div className='drop-message'>Drop files here to upload</div>
                            </div>
                        )}
                        <div></div>
                    </div>
                    {
                        // allow the users to activate specific plugins (e.g. web search)
                        <PluginsMenu plugins={activeGpt?.plugins ?? []} pluginsChanged={activePluginsChanged} />
                    }
                    <SendButton mode={getSendButtonMode()} onClick={() => handleSendQuery()} onChangeQuery={onChangeQueryExternal} onSendQuery={handleSendQuery} currentQuery={editorQuery} />
                </div>
                <ModelSelection disabled={hideModelSelection} changeModel={handleModelChange} />
            </div>
        </div>
    );
};

interface WarningMessage {
    text: string;
    link?: string;
}
